import * as React from 'react';

import moment from 'moment';

import type {Assignable} from 'nutshell-core/types';

import {colors} from 'shells/colors/colors';
import {Stack} from 'shells/layout/stack/stack';

import {appHistory} from '../../../../../../history';

import {Button} from '../../button/button';
import {CalendarSquareRegularActivityIcon} from '../../icon/svg/calendar-square-regular-activity-icon';
import {RichTextDisplay} from '../../rich-text-display/rich-text-display';
import {Body} from '../../typography/body';

import {TaskAssigneeSelector} from './task-assignee-selector';
import {TaskDelayStatus} from './task-delay-status';
import {TaskStatusLabel} from './task-status-label';
import type {TaskRowItem} from './types';

import styles from './task-row.module.css';

type Props = {
    task: TaskRowItem;
    assignables: Assignable[];
    getAssigneeRef: (arg1?: HTMLElement | null | undefined) => void;
    setIsAssigneePopoverOpen: (isAssigneePopoverOpen: boolean) => void;
    onUpdateAssignee: (assigneeId?: string | null | undefined) => void;
    handleDueTimeChange: (dueTime: number) => void;
    hideCreatorEntity?: boolean;
};

export function TaskRowContent(props: Props) {
    const {task, handleDueTimeChange} = props;

    const [isDescriptionExpanded, setIsDescriptionExpanded] = React.useState<boolean>(false);
    const [shouldShowReadMore, setShouldShowReadMore] = React.useState<boolean>(false);
    const descriptionContainerRef = React.useRef<HTMLDivElement | null | undefined>(null);
    const taskContentRef = React.useRef<HTMLDivElement | null | undefined>(null);

    const description = task.linkifiedDescription || task.description;

    React.useEffect(() => {
        const checkTruncation = () => {
            if (descriptionContainerRef.current && taskContentRef.current) {
                const taskDescWidth = descriptionContainerRef.current.scrollWidth;
                const taskContentWidth = taskContentRef.current.offsetWidth;

                const isTruncatedHorizontally = taskDescWidth >= taskContentWidth;
                const hasNewlines = description && description.toLowerCase().includes('<br');

                setShouldShowReadMore(Boolean(isTruncatedHorizontally || hasNewlines));
            }
        };

        checkTruncation();

        window.addEventListener('resize', checkTruncation);

        return () => {
            window.removeEventListener('resize', checkTruncation);
        };
    }, [description]);

    return (
        <>
            {/* @ts-expect-error */}
            <div ref={taskContentRef} className={styles['task-row-status']}>
                <TaskStatusLabel
                    dueTime={task.dueTime}
                    isOverdue={task.isOverdue}
                    completedBy={task.completedByUser && task.completedByUser.name}
                    completedTime={task.completedTime}
                    isSkipped={task.isSkipped}
                    onDueTimeChange={handleDueTimeChange}
                    isAutomated={task.__typename === 'Step'}
                />
                {!task.isCompleted && !task.isSkipped && (
                    <TaskAssigneeSelector
                        assignables={props.assignables}
                        assigneeId={task.assignee && task.assignee.id}
                        getAssigneeRef={props.getAssigneeRef}
                        onAssigneePopoverOpen={props.setIsAssigneePopoverOpen}
                        // Don't allow clearing a recurring task's assignee
                        canClear={task.__typename !== 'Task' || task.recurringPeriod == null}
                        onUpdateAssignee={props.onUpdateAssignee}
                    />
                )}
            </div>
            {!props.hideCreatorEntity &&
                NutClientConfig.hasAiFollowUpTasksFF &&
                task.__typename === 'Task' &&
                task.creatorEntity && (
                    <Stack isHorizontal={true} spacing={2} alignItems='center'>
                        <CalendarSquareRegularActivityIcon
                            // @ts-expect-error
                            color={colors.aiPurplePrimary}
                            size={16}
                            alignCenter={true}
                        />
                        via
                        <div className={styles['task-row-creator-entity']}>
                            <Button
                                name={task.creatorEntity.name}
                                variant='text-primary'
                                onClick={() => {
                                    appHistory.replace({
                                        ...appHistory.location,
                                        hash: `/${task.creatorEntity?.id}`,
                                        state: {modal: true},
                                    });
                                }}
                            >
                                {task.creatorEntity.name}
                            </Button>
                        </div>
                    </Stack>
                )}
            {task.__typename === 'Task' &&
                task.assigner &&
                task.assignedTime &&
                task.assignee &&
                task.assignee.id !== task.assigner.id && (
                    <Body
                        size='small'
                        color='light'
                        title={`Assigned on ${moment
                            .unix(task.assignedTime)
                            .format('MMM Do, YYYY, h:mm A')}`}
                    >
                        Assigned by {task.assigner && task.assigner.name}
                    </Body>
                )}
            {description && (
                // Used to keep track of the width of the description text for truncation
                // @ts-expect-error
                <div ref={descriptionContainerRef} className={styles['description-container']}>
                    <RichTextDisplay
                        html={description}
                        size='small'
                        smallLineHeight={true}
                        color={task.isCompleted ? 'light' : 'medium'}
                        truncateInline={!isDescriptionExpanded}
                        maxLines={isDescriptionExpanded ? undefined : 1}
                        shouldBreakWords={true}
                    />
                    {shouldShowReadMore && (
                        <div className={styles['read-more-button']}>
                            <Button
                                variant='text-secondary-dark'
                                size='small'
                                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                                noPadding={true}
                            >
                                {isDescriptionExpanded ? 'Read less' : 'Read more'}
                            </Button>
                        </div>
                    )}
                </div>
            )}
            {!task.isCompleted &&
                !task.isSkipped &&
                task.delayApplications &&
                task.delayApplications.map((delayApplication) => (
                    <TaskDelayStatus
                        key={delayApplication.id}
                        delayedAt={delayApplication.appliedTime}
                        delayedBy={
                            delayApplication && delayApplication.user && delayApplication.user.name
                        }
                        delayFor={
                            delayApplication &&
                            delayApplication.delay &&
                            delayApplication.delay.displayLength
                        }
                        delayReason={delayApplication.note}
                    />
                ))}
        </>
    );
}
